/**
 * SRS / RoofHub payment-history CSV import (Sep 14, 2026) — the UI half of
 * lib/materialPayments.ts. Lives inside the Material invoices panel on
 * /financials.
 *
 * Flow: pick the CSV → parse + auto-map columns in the browser → the server
 * returns a PLAN (dry run: every line matched to its invoice and classified)
 * → review grouped by payment, untick anything → Apply runs the SAME RPC for
 * real → result banner with a one-click Undo for that batch.
 *
 * Nothing is written before Apply. Re-importing an overlapping export is a
 * no-op (every line is deduped server-side).
 */

import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { supabase } from "../../lib/supabase";
import { fmtUSD } from "../../lib/analytics";
import { parseCsv } from "../../lib/csvParse";
import {
  IMPORT_FIELDS, OUTCOME_META, buildLines, detectColumns, runPaymentImport, toRpcLines, undoPaymentImport,
  type ColumnMap, type ImportPlan, type PlanLine, type PlanPayment, type RawLine,
} from "../../lib/materialPayments";

function fmtDateUS(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = iso.slice(0, 10);
  return `${d.slice(5, 7)}/${d.slice(8, 10)}/${d.slice(0, 4)}`;
}

const TONE: Record<string, string> = {
  emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
  slate: "bg-slate-100 text-slate-600 border-slate-200",
  amber: "bg-amber-50 text-amber-700 border-amber-200",
  sky: "bg-sky-50 text-sky-700 border-sky-200",
  rose: "bg-rose-50 text-rose-700 border-rose-200",
};

function Chip({ tone, children }: { tone: keyof typeof TONE; children: ReactNode }) {
  return <span className={`inline-block px-2 py-0.5 rounded-full border ${TONE[tone]}`}>{children}</span>;
}

function lineNote(l: PlanLine, raw: RawLine | undefined): string {
  switch (l.outcome) {
    case "mark_paid":
      return (l.applied_before ?? 0) !== 0
        ? `earlier ${fmtUSD(l.applied_before ?? 0)} + this line covers it · paid ${fmtDateUS(l.payment_date)}`
        : `paid ${fmtDateUS(l.payment_date)}`;
    case "already_paid":
      if (l.fills_paid_at) return `records paid date ${fmtDateUS(l.payment_date)}`;
      if (l.paid_at_before) return `paid ${fmtDateUS(l.paid_at_before)} on file`;
      return l.covered_after === false
        ? `invoice ${fmtUSD(l.invoice_total ?? 0)} · this ${fmtUSD(l.amount ?? 0)} recorded only`
        : "";
    case "partial": return `${fmtUSD(l.applied_after ?? 0)} of ${fmtUSD(l.invoice_total ?? 0)} applied`;
    case "create_stub": return `invoice ${fmtUSD(l.stub_total ?? l.amount ?? 0)} · paid ${fmtDateUS(l.payment_date)}`;
    case "skip_duplicate": return "this line was already imported";
    case "skip_status": return `status "${l.status ?? ""}"`;
    case "skip_unmatched": return "turn on “create invoices” below to add it";
    case "skip_ambiguous": return "more than one invoice shares this number";
    case "skip_invalid": return raw?.problems.join(" · ") ?? "";
    default: return "";
  }
}

interface ApplyResult { plan: ImportPlan; fileName: string }

export default function MaterialPaymentsImport({ onChanged }: { onChanged: () => void }) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<string[][]>([]);
  const [map, setMap] = useState<ColumnMap>({});
  const [showMap, setShowMap] = useState(false);
  const [createMissing, setCreateMissing] = useState(true);
  const [plan, setPlan] = useState<ImportPlan | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState<"read" | "plan" | "apply" | "undo" | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [result, setResult] = useState<ApplyResult | null>(null);

  const lines: RawLine[] = useMemo(
    () => (headers.length && rows.length ? buildLines(rows, headers, map) : []),
    [rows, headers, map]);
  const missingRequired = useMemo(
    () => IMPORT_FIELDS.filter((f) => f.required && map[f.key] == null),
    [map]);

  function reset() {
    setFile(null); setHeaders([]); setRows([]); setMap({}); setPlan(null);
    setSelected(new Set()); setErr(null);
  }

  async function handleFile(files: FileList | null) {
    const f = files?.[0];
    if (fileRef.current) fileRef.current.value = "";
    if (!f) return;
    reset(); setNotice(null); setResult(null);
    setBusy("read");
    try {
      const parsed = parseCsv(await f.text());
      if (parsed.length < 2) throw new Error("That file has no data rows — in RoofHub open Payment History, export it as CSV, and try again.");
      const hdr = parsed[0].map((h) => h.trim());
      const m = detectColumns(hdr);
      setHeaders(hdr); setRows(parsed.slice(1)); setMap(m); setFile(f);
      setShowMap(IMPORT_FIELDS.some((x) => x.required && m[x.key] == null));
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Couldn't read that file.");
    } finally {
      setBusy(null);
    }
  }

  // Dry run whenever the parsed lines or the options change — the server does
  // the matching, and the table below renders exactly its plan.
  useEffect(() => {
    if (!file || lines.length === 0 || missingRequired.length > 0) { setPlan(null); return; }
    let cancelled = false;
    setBusy("plan"); setErr(null);
    runPaymentImport(toRpcLines(lines), { create_missing: createMissing, file_name: file.name }, true)
      .then((p) => {
        if (cancelled) return;
        setPlan(p);
        setSelected(new Set(p.lines.filter((l) => OUTCOME_META[l.outcome].selectable).map((l) => l.idx)));
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setPlan(null);
        setErr(e instanceof Error ? e.message : "Couldn't build the import plan.");
      })
      .finally(() => { if (!cancelled) setBusy(null); });
    return () => { cancelled = true; };
  }, [file, lines, createMissing, missingRequired.length]);

  const groups = useMemo(() => {
    if (!plan) return [];
    const pay = new Map(plan.payments.map((p) => [`${p.confirmation_number}|${p.payment_date}`, p]));
    const byKey = new Map<string, { key: string; payment: PlanPayment | undefined; lines: PlanLine[] }>();
    for (const l of plan.lines) {
      const key = `${l.confirmation_number}|${l.payment_date ?? ""}`;
      let g = byKey.get(key);
      if (!g) { g = { key, payment: pay.get(key), lines: [] }; byKey.set(key, g); }
      g.lines.push(l);
    }
    return [...byKey.values()].sort((a, b) =>
      (a.lines[0].payment_date ?? "").localeCompare(b.lines[0].payment_date ?? "") || a.key.localeCompare(b.key));
  }, [plan]);

  // What the CURRENT selection would do (the server re-derives on apply; this
  // only drifts if one of two lines paying the same invoice is unticked).
  const sel = useMemo(() => {
    if (!plan) return null;
    const chosen = plan.lines.filter((l) => selected.has(l.idx));
    const invTotals = new Map<string, number>();
    let already = 0, partial = 0, stubs = 0, applied = 0;
    for (const l of chosen) {
      applied += l.amount ?? 0;
      if (l.outcome === "mark_paid" && l.invoice_id) invTotals.set(l.invoice_id, l.invoice_total ?? 0);
      else if (l.outcome === "already_paid") already++;
      else if (l.outcome === "partial") partial++;
      else if (l.outcome === "create_stub") stubs++;
    }
    let markPaidTotal = 0;
    invTotals.forEach((v) => { markPaidTotal += v; });
    return { count: chosen.length, markPaid: invTotals.size, markPaidTotal, already, partial, stubs, applied };
  }, [plan, selected]);

  const selectableIdx = useMemo(
    () => (plan ? plan.lines.filter((l) => OUTCOME_META[l.outcome].selectable).map((l) => l.idx) : []),
    [plan]);
  const allOn = selectableIdx.length > 0 && selectableIdx.every((i) => selected.has(i));

  function toggle(idx: number) {
    setSelected((prev) => { const n = new Set(prev); if (n.has(idx)) n.delete(idx); else n.add(idx); return n; });
  }
  function toggleAll() {
    setSelected(allOn ? new Set() : new Set(selectableIdx));
  }

  async function apply() {
    if (!plan || !file || !sel || sel.count === 0) return;
    setBusy("apply"); setErr(null);
    try {
      // archive the CSV next to the invoice PDFs (audit trail; best-effort)
      const path = `material-payments/${Date.now()}-${file.name.replace(/[^A-Za-z0-9._-]/g, "_")}`;
      const up = await supabase.storage.from("deal-documents").upload(path, file, { contentType: "text/csv" });
      const all = toRpcLines(lines);
      const subset = plan.lines.filter((l) => selected.has(l.idx)).map((l) => all[l.idx]);
      const res = await runPaymentImport(subset, {
        create_missing: createMissing,
        file_name: file.name,
        storage_path: up.error ? null : path,
        batch_id: plan.batch_id,
      }, false);
      setResult({ plan: res, fileName: file.name });
      reset();
      onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Import failed — nothing was written.");
    } finally {
      setBusy(null);
    }
  }

  async function undo() {
    if (!result) return;
    if (!window.confirm(`Undo the import of ${result.fileName}? Every invoice it marked paid goes back to unpaid and its payment lines are removed.`)) return;
    setBusy("undo"); setErr(null);
    try {
      const r = await undoPaymentImport(result.plan.batch_id);
      setNotice(`Undone — removed ${r.deleted_payments} payment line${r.deleted_payments === 1 ? "" : "s"}` +
        (r.deleted_invoices ? ` and ${r.deleted_invoices} created invoice${r.deleted_invoices === 1 ? "" : "s"}` : "") + ".");
      setResult(null);
      onChanged();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Undo failed.");
    } finally {
      setBusy(null);
    }
  }

  const s = plan?.summary;
  const rs = result?.plan.summary;

  return (
    <div className="px-4 pt-3">
      <input ref={fileRef} type="file" accept=".csv,text/csv,text/plain" className="hidden"
        onChange={(e) => void handleFile(e.target.files)} />
      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={() => fileRef.current?.click()} disabled={busy !== null}
          className="px-3 py-2 rounded-lg border border-emerald-300 bg-emerald-50 text-emerald-800 text-[12.5px] font-bold hover:bg-emerald-100 disabled:opacity-50">
          {busy === "read" ? "Reading…" : "💳 Import SRS payments (CSV)"}
        </button>
        <span className="text-[11px] text-slate-500">
          RoofHub → Payment History → export CSV. Each line is matched to its invoice by number; nothing is written until you apply.
        </span>
      </div>

      {notice && (
        <div className="mt-2 px-3 py-2 rounded-lg bg-slate-50 border border-slate-200 text-slate-700 text-[12.5px]">{notice}</div>
      )}
      {err && (
        <div className="mt-2 px-3 py-2 rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-[12.5px]">{err}</div>
      )}

      {result && rs && (
        <div className="mt-2 px-3 py-2.5 rounded-xl bg-emerald-50 border border-emerald-200 text-[12.5px] text-emerald-900">
          <div className="font-extrabold">
            ✓ Imported {rs.inserted_payments} payment line{rs.inserted_payments === 1 ? "" : "s"} from {result.fileName}
          </div>
          <div className="mt-0.5 text-emerald-800">
            {rs.mark_paid_invoices} invoice{rs.mark_paid_invoices === 1 ? "" : "s"} marked paid ({fmtUSD(rs.mark_paid_total)})
            {rs.already_paid > 0 && <> · {rs.already_paid} already paid{rs.fills_paid_at > 0 ? ` (${rs.fills_paid_at} got their paid date)` : ""}</>}
            {rs.partial > 0 && <> · {rs.partial} partial payment{rs.partial === 1 ? "" : "s"}</>}
            {rs.created_invoices > 0 && <> · {rs.created_invoices} new invoice{rs.created_invoices === 1 ? "" : "s"} created — match {rs.created_invoices === 1 ? "it" : "them"} to a job in the Unmatched filter</>}
            {rs.skip_duplicate > 0 && <> · {rs.skip_duplicate} already imported</>}
          </div>
          <div className="mt-1.5 flex gap-3 items-center">
            <button onClick={() => void undo()} disabled={busy !== null}
              className="text-[11.5px] font-bold text-rose-700 hover:underline disabled:opacity-50">
              {busy === "undo" ? "Undoing…" : "Undo this import"}
            </button>
            <button onClick={() => setResult(null)} className="text-[11.5px] text-slate-500 hover:underline">dismiss</button>
          </div>
        </div>
      )}

      {file && (
        <div className="mt-3 border border-emerald-200 bg-emerald-50/30 rounded-xl p-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="text-[13px] font-extrabold">
              Review payments — {file.name}
              <span className="ml-2 text-[11px] font-normal text-slate-500">{rows.length} line{rows.length === 1 ? "" : "s"}</span>
            </div>
            <button onClick={reset} className="text-[11.5px] text-slate-500 hover:underline">cancel</button>
          </div>

          <details open={showMap} onToggle={(e) => setShowMap(e.currentTarget.open)} className="mt-2">
            <summary className="cursor-pointer text-[11px] font-bold uppercase tracking-wide text-slate-500">
              Column mapping{" "}
              {missingRequired.length > 0
                ? <span className="text-rose-600 normal-case">— {missingRequired.map((f) => f.label).join(", ")} not found · pick the column</span>
                : <span className="normal-case font-normal">· auto-detected, click to check</span>}
            </summary>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2 text-[12px]">
              {IMPORT_FIELDS.map((f) => (
                <label key={f.key} className="flex flex-col gap-0.5">
                  <span className="text-[10px] font-bold uppercase tracking-wide text-slate-500">{f.label}{f.required ? " *" : ""}</span>
                  <select value={map[f.key] ?? ""}
                    onChange={(e) => setMap({ ...map, [f.key]: e.target.value === "" ? undefined : Number(e.target.value) })}
                    className="border border-slate-300 rounded-lg px-2 py-1 bg-white">
                    <option value="">— not in file —</option>
                    {headers.map((h, i) => <option key={i} value={i}>{h || `(column ${i + 1})`}</option>)}
                  </select>
                  <span className="text-[10px] text-slate-400">{f.hint}</span>
                </label>
              ))}
            </div>
          </details>

          <label className="flex items-start gap-2 text-[12px] mt-2 cursor-pointer">
            <input type="checkbox" checked={createMissing} onChange={(e) => setCreateMissing(e.target.checked)} className="mt-0.5" />
            <span>
              Create invoices for lines the CRM doesn't have yet — saved as paid and <b>unmatched</b>, so the money still counts
              in company COGS and they land in the Unmatched queue to match to a job.
            </span>
          </label>

          {busy === "plan" && <div className="mt-3 text-[12px] text-slate-500">Matching against the invoice register…</div>}

          {plan && s && (
            <>
              <div className="mt-3 flex flex-wrap gap-1.5 text-[11px] font-bold">
                <Chip tone="emerald">{s.mark_paid_invoices} to mark paid · {fmtUSD(s.mark_paid_total)}</Chip>
                {s.already_paid > 0 && <Chip tone="slate">{s.already_paid} already paid{s.fills_paid_at ? ` · ${s.fills_paid_at} get a paid date` : ""}</Chip>}
                {s.partial > 0 && <Chip tone="amber">{s.partial} partial</Chip>}
                {s.create_stub > 0 && <Chip tone="sky">{s.create_stub} not in CRM → new invoice{s.create_stub === 1 ? "" : "s"}</Chip>}
                {s.skip_unmatched > 0 && <Chip tone="amber">{s.skip_unmatched} not in CRM · skipped</Chip>}
                {s.skip_duplicate > 0 && <Chip tone="slate">{s.skip_duplicate} already imported</Chip>}
                {s.skip_status > 0 && <Chip tone="rose">{s.skip_status} not complete</Chip>}
                {s.skip_ambiguous > 0 && <Chip tone="rose">{s.skip_ambiguous} ambiguous</Chip>}
                {s.skip_invalid > 0 && <Chip tone="rose">{s.skip_invalid} unreadable</Chip>}
              </div>

              {plan.payments.length > 0 && (
                <div className="mt-2 grid sm:grid-cols-2 lg:grid-cols-3 gap-1.5">
                  {plan.payments.map((p) => (
                    <div key={`${p.confirmation_number}|${p.payment_date}`}
                      className={`rounded-lg border px-2.5 py-1.5 text-[11.5px] ${p.reconciles ? "border-slate-200 bg-white" : "border-amber-300 bg-amber-50"}`}>
                      <div className="font-bold">Payment {p.confirmation_number || "(no confirmation #)"} · {fmtDateUS(p.payment_date)}</div>
                      <div className="text-slate-600">
                        {p.line_count} line{p.line_count === 1 ? "" : "s"} sum to {fmtUSD(p.line_sum)}
                        {p.payment_total != null ? ` · payment ${fmtUSD(p.payment_total)}` : ""}{" "}
                        {p.reconciles
                          ? <span className="text-emerald-700 font-bold">✓</span>
                          : <span className="text-amber-700 font-bold">⚠ lines don't add up to the payment</span>}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-3 overflow-x-auto max-h-[520px] overflow-y-auto border border-slate-200 rounded-lg bg-white">
                <table className="w-full text-[12px] min-w-[780px]">
                  <thead className="sticky top-0 bg-slate-50 z-10">
                    <tr className="text-left text-[10px] uppercase tracking-wide text-slate-500 border-b border-slate-200">
                      <th className="py-1.5 px-2 w-8"><input type="checkbox" checked={allOn} onChange={toggleAll} aria-label="select all" /></th>
                      <th className="px-2">Invoice</th>
                      <th className="px-2">Job</th>
                      <th className="px-2 text-right">Applied</th>
                      <th className="px-2 text-right">Invoice total</th>
                      <th className="px-2">Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    {groups.map((g) => (
                      <Fragment key={g.key}>
                        <tr className="bg-slate-50 border-b border-slate-200">
                          <td colSpan={6} className="px-2 py-1 text-[11px] font-bold text-slate-600">
                            💳 Payment {g.lines[0].confirmation_number || "(no confirmation #)"} · {fmtDateUS(g.lines[0].payment_date)} · {g.lines.length} line{g.lines.length === 1 ? "" : "s"}
                            {g.payment?.payment_total != null ? ` · ${fmtUSD(g.payment.payment_total)}` : ""}
                          </td>
                        </tr>
                        {g.lines.map((l) => {
                          const meta = OUTCOME_META[l.outcome];
                          const on = selected.has(l.idx);
                          const cm = (l.amount ?? 0) < 0;
                          return (
                            <tr key={l.idx} className={`border-b border-slate-100 align-top ${meta.selectable && !on ? "opacity-50" : ""}`}>
                              <td className="px-2 py-1.5">
                                {meta.selectable
                                  ? <input type="checkbox" checked={on} onChange={() => toggle(l.idx)} />
                                  : <span className="text-slate-300">—</span>}
                              </td>
                              <td className="px-2 py-1.5 whitespace-nowrap">
                                <div className="font-bold">{l.invoice_number || <span className="text-slate-400">(blank)</span>}</div>
                                {(l.match === "normalized" || l.match === "base") && (
                                  <div className="text-[10.5px] text-slate-500" title="matched by number, ignoring leading zeros / suffix">≈ {l.invoice_number_canonical}</div>
                                )}
                              </td>
                              <td className="px-2 py-1.5">
                                <div className="font-semibold">
                                  {l.homeowner_name ?? l.job_name ?? (l.outcome === "create_stub" ? "new invoice — match to a job after" : "—")}
                                </div>
                                {l.job_address && <div className="text-[10.5px] text-slate-500 truncate max-w-[260px]">{l.job_address}</div>}
                              </td>
                              <td className={`px-2 py-1.5 text-right tabular-nums font-bold whitespace-nowrap ${cm ? "text-emerald-700" : ""}`}>
                                {l.amount != null ? fmtUSD(l.amount) : "—"}{cm && <span className="text-[10px] font-normal"> CM</span>}
                              </td>
                              <td className="px-2 py-1.5 text-right tabular-nums whitespace-nowrap">
                                {l.invoice_total != null ? fmtUSD(l.invoice_total) : "—"}
                              </td>
                              <td className="px-2 py-1.5">
                                <span className={`inline-block px-2 py-0.5 rounded-full border text-[10.5px] font-bold ${TONE[meta.tone]}`}>{meta.label}</span>
                                <span className="ml-1.5 text-[11px] text-slate-500">{lineNote(l, lines[l.idx])}</span>
                              </td>
                            </tr>
                          );
                        })}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <button onClick={() => void apply()} disabled={busy !== null || !sel || sel.count === 0}
                  className="px-3.5 py-2 rounded-lg bg-emerald-600 text-white text-[12.5px] font-bold hover:bg-emerald-700 disabled:opacity-50">
                  {busy === "apply" ? "Applying…" : `Apply ${sel?.count ?? 0} line${sel?.count === 1 ? "" : "s"}`}
                </button>
                <button onClick={reset} className="px-3.5 py-2 rounded-lg border border-slate-300 text-slate-600 text-[12.5px] font-bold">Cancel</button>
                {sel && (
                  <span className="text-[11.5px] text-slate-600">
                    {sel.markPaid} invoice{sel.markPaid === 1 ? "" : "s"} → paid ({fmtUSD(sel.markPaidTotal)})
                    {sel.already ? ` · ${sel.already} already paid` : ""}
                    {sel.partial ? ` · ${sel.partial} partial` : ""}
                    {sel.stubs ? ` · ${sel.stubs} new` : ""}
                    {" · "}{fmtUSD(sel.applied)} applied
                  </span>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
